import { palette } from "./palette.js";
import { radius } from "./radius.js";
import { spacing } from "./spacing.js";
import { typography } from "./typography.js";

export const directionBWarmth = {
  appBackground: palette.sky[25],
  panelBackground: palette.sky[50],
  paleBlue: palette.sky[100],
  borderBlue: palette.sky[200],
  cardBackground: palette.neutral[0],
  textPrimary: palette.sky[950],
  textSecondary: palette.neutral[500],
  textQuiet: palette.neutral[400],
  skyPrimary: palette.sky[600],
  skyDeep: palette.sky[700],
  tealAccent: palette.teal[550],
  primaryAction: palette.sky[600],
  secondaryAction: palette.sky[700],
  primaryGradient: "linear-gradient(135deg, #0284C7, #0D9488)",
  shellGradient: "linear-gradient(90deg, #0284C7, #0369A1)",
  onGradientLogoBackground: "rgba(255, 255, 255, 0.92)",
  onGradientPillBackground: "rgba(255, 255, 255, 0.18)",
  onGradientPillBorder: "rgba(255, 255, 255, 0.28)",
  onGradientAvatarBackground: "rgba(255, 255, 255, 0.22)",
  onGradientAvatarBorder: "rgba(255, 255, 255, 0.34)",
  onGradientAvatarText: palette.neutral[0],
  modalHeaderBackground: palette.sky[50],
  cardShadow: "0 2px 12px rgba(2,132,199,.06)",
  shellShadow: "0 16px 48px rgba(2,132,199,.12)",
  status: {
    danger: {
      background: "#fee2e2",
      text: "#dc2626",
      border: "#fecaca",
    },
    info: {
      background: palette.blue[100],
      text: palette.blue[600],
      border: palette.blue[200],
    },
    success: {
      background: palette.emerald[100],
      text: palette.emerald[600],
      border: palette.emerald[200],
    },
    warning: {
      background: palette.amber[100],
      text: palette.amber[600],
      border: palette.amber[200],
    },
  },
  radius: {
    card: radius.xl,
    modal: radius["2xl"],
    control: radius.md,
    pill: radius.pill,
  },
} as const;

const lightStatusChip = {
  danger: {
    background: palette.rose[100],
    text: palette.rose[600],
    border: palette.rose[200],
  },
  info: {
    background: palette.blue[100],
    text: palette.blue[600],
    border: palette.blue[200],
  },
  success: {
    background: palette.emerald[100],
    text: palette.emerald[600],
    border: palette.emerald[200],
  },
  warning: {
    background: palette.amber[100],
    text: palette.amber[600],
    border: palette.amber[200],
  },
} as const;

const darkStatusChip = {
  danger: {
    background: "rgba(225, 29, 72, 0.18)",
    text: palette.rose[300],
    border: "rgba(253, 164, 175, 0.34)",
  },
  info: {
    background: "rgba(37, 99, 235, 0.18)",
    text: palette.blue[300],
    border: "rgba(147, 197, 253, 0.34)",
  },
  success: {
    background: "rgba(5, 150, 105, 0.18)",
    text: palette.emerald[300],
    border: "rgba(110, 231, 183, 0.34)",
  },
  warning: {
    background: "rgba(217, 119, 6, 0.18)",
    text: palette.amber[300],
    border: "rgba(252, 211, 77, 0.34)",
  },
} as const;

const workforceWarmStatusChip = {
  danger: {
    background: palette.rose[100],
    text: palette.rose[600],
    border: palette.rose[200],
  },
  info: {
    background: palette.clinicalBlue[100],
    text: palette.clinicalBlue[700],
    border: palette.clinicalBlue[200],
  },
  success: {
    background: palette.sage[100],
    text: palette.sage[600],
    border: palette.sage[200],
  },
  warning: {
    background: palette.amber[100],
    text: palette.amber[700],
    border: palette.amber[200],
  },
} as const;

export const semanticTheme = {
  light: {
    background: palette.neutral[50],
    backgroundSubtle: palette.neutral[100],
    surface: palette.neutral[0],
    surfaceElevated: palette.neutral[0],
    surfaceMuted: palette.neutral[100],
    textPrimary: palette.neutral[900],
    textSecondary: palette.neutral[600],
    textTertiary: palette.neutral[500],
    primaryInverseText: palette.neutral[0],
    borderSubtle: palette.neutral[200],
    borderStrong: palette.neutral[300],
    accent: palette.teal[600],
    accentSubtle: palette.teal[100],
    accentStrong: palette.teal[700],
    accentContrast: palette.neutral[0],
    info: palette.blue[600],
    success: palette.emerald[600],
    warning: palette.amber[600],
    danger: palette.rose[600],
    criticalText: palette.rose[700],
    headerBand: palette.neutral[900],
    headerText: palette.neutral[0],
    headerTextMuted: palette.neutral[300],
    backdropOverlay: "rgba(248, 250, 252, 0.88)",
    focusRing: palette.teal[400],
    primaryGradient: `linear-gradient(135deg, ${palette.blue[600]}, ${palette.teal[600]})`,
    shellGradient: `linear-gradient(90deg, ${palette.neutral[900]}, ${palette.neutral[700]})`,
    onGradientLogoBackground: "rgba(255, 255, 255, 0.92)",
    onGradientPillBackground: "rgba(255, 255, 255, 0.16)",
    onGradientPillBorder: "rgba(255, 255, 255, 0.24)",
    onGradientAvatarBackground: "rgba(255, 255, 255, 0.18)",
    onGradientAvatarBorder: "rgba(255, 255, 255, 0.3)",
    onGradientAvatarText: palette.neutral[0],
    modalHeaderBackground: palette.neutral[50],
    cardShadow: "0 2px 12px rgba(15, 23, 42, 0.05)",
    shellShadow: "0 16px 48px rgba(15, 23, 42, 0.12)",
    statusChip: lightStatusChip,
  },
  dark: {
    background: palette.neutral[900],
    backgroundSubtle: palette.neutral[800],
    surface: palette.neutral[800],
    surfaceElevated: palette.neutral[700],
    surfaceMuted: palette.neutral[700],
    textPrimary: palette.neutral[50],
    textSecondary: palette.neutral[300],
    textTertiary: palette.neutral[400],
    primaryInverseText: palette.neutral[0],
    borderSubtle: palette.neutral[700],
    borderStrong: palette.neutral[600],
    accent: palette.teal[300],
    accentSubtle: palette.teal[900],
    accentStrong: palette.teal[200],
    accentContrast: palette.neutral[900],
    info: palette.blue[300],
    success: palette.emerald[300],
    warning: palette.amber[300],
    danger: palette.rose[300],
    criticalText: palette.rose[200],
    headerBand: palette.neutral[900],
    headerText: palette.neutral[0],
    headerTextMuted: palette.neutral[300],
    backdropOverlay: "rgba(15, 23, 42, 0.76)",
    focusRing: palette.teal[200],
    primaryGradient: `linear-gradient(135deg, ${palette.blue[500]}, ${palette.teal[500]})`,
    shellGradient: `linear-gradient(90deg, ${palette.neutral[900]}, ${palette.neutral[800]})`,
    onGradientLogoBackground: "rgba(255, 255, 255, 0.9)",
    onGradientPillBackground: "rgba(255, 255, 255, 0.14)",
    onGradientPillBorder: "rgba(255, 255, 255, 0.2)",
    onGradientAvatarBackground: "rgba(255, 255, 255, 0.18)",
    onGradientAvatarBorder: "rgba(255, 255, 255, 0.28)",
    onGradientAvatarText: palette.neutral[0],
    modalHeaderBackground: palette.neutral[800],
    cardShadow: "0 2px 12px rgba(0, 0, 0, 0.24)",
    shellShadow: "0 16px 48px rgba(0, 0, 0, 0.34)",
    statusChip: darkStatusChip,
  },
  workforceWarm: {
    background: palette.warmNeutral[50],
    backgroundSubtle: palette.clinicalBlue[50],
    surface: palette.warmNeutral[0],
    surfaceElevated: "rgba(255, 253, 250, 0.96)",
    surfaceMuted: "rgba(240, 247, 255, 0.78)",
    textPrimary: palette.navy[900],
    textSecondary: palette.navy[800],
    textTertiary: palette.neutral[500],
    primaryInverseText: palette.neutral[0],
    borderSubtle: "#e7edf5",
    borderStrong: palette.clinicalBlue[200],
    accent: palette.navy[600],
    accentSubtle: palette.clinicalBlue[100],
    accentStrong: palette.navy[800],
    accentContrast: palette.neutral[0],
    info: palette.clinicalBlue[700],
    success: palette.sage[600],
    warning: palette.amber[700],
    danger: palette.rose[600],
    criticalText: palette.rose[700],
    headerBand: palette.navy[900],
    headerText: palette.neutral[0],
    headerTextMuted: palette.clinicalBlue[100],
    backdropOverlay: "rgba(250, 247, 240, 0.88)",
    focusRing: palette.clinicalBlue[300],
    primaryGradient: `linear-gradient(135deg, ${palette.navy[700]}, ${palette.sage[600]})`,
    shellGradient: `linear-gradient(90deg, ${palette.navy[900]}, ${palette.navy[700]})`,
    onGradientLogoBackground: "rgba(255, 253, 250, 0.92)",
    onGradientPillBackground: "rgba(255, 253, 250, 0.16)",
    onGradientPillBorder: "rgba(255, 253, 250, 0.28)",
    onGradientAvatarBackground: "rgba(255, 253, 250, 0.2)",
    onGradientAvatarBorder: "rgba(255, 253, 250, 0.34)",
    onGradientAvatarText: palette.neutral[0],
    modalHeaderBackground: palette.clinicalBlue[50],
    cardShadow: "0 2px 12px rgba(23, 40, 70, 0.06)",
    shellShadow: "0 16px 48px rgba(23, 40, 70, 0.12)",
    statusChip: workforceWarmStatusChip,
  },
  workforceBlueGrey: {
    background: palette.neutral[50],
    backgroundSubtle: "#edf4fb",
    surface: palette.neutral[0],
    surfaceElevated: "rgba(255, 255, 255, 0.96)",
    surfaceMuted: "rgba(241, 245, 249, 0.82)",
    textPrimary: palette.neutral[900],
    textSecondary: palette.neutral[700],
    textTertiary: palette.neutral[500],
    primaryInverseText: palette.neutral[0],
    borderSubtle: palette.neutral[200],
    borderStrong: palette.ample[2],
    accent: palette.ample[7],
    accentSubtle: palette.ample[1],
    accentStrong: palette.ample[9],
    accentContrast: palette.neutral[0],
    info: palette.blue[700],
    success: palette.sage[600],
    warning: palette.amber[700],
    danger: palette.rose[600],
    criticalText: palette.rose[700],
    headerBand: palette.neutral[900],
    headerText: palette.neutral[0],
    headerTextMuted: palette.neutral[300],
    backdropOverlay: "rgba(248, 250, 252, 0.88)",
    focusRing: palette.blue[300],
    primaryGradient: `linear-gradient(135deg, ${palette.ample[7]}, ${palette.clinicalBlue[600]})`,
    shellGradient: `linear-gradient(90deg, ${palette.neutral[900]}, ${palette.ample[9]})`,
    onGradientLogoBackground: "rgba(255, 255, 255, 0.92)",
    onGradientPillBackground: "rgba(255, 255, 255, 0.15)",
    onGradientPillBorder: "rgba(255, 255, 255, 0.26)",
    onGradientAvatarBackground: "rgba(255, 255, 255, 0.2)",
    onGradientAvatarBorder: "rgba(255, 255, 255, 0.32)",
    onGradientAvatarText: palette.neutral[0],
    modalHeaderBackground: palette.ample[0],
    cardShadow: "0 2px 12px rgba(45, 74, 123, 0.06)",
    shellShadow: "0 16px 48px rgba(45, 74, 123, 0.12)",
    statusChip: workforceWarmStatusChip,
  },
  workforceWarmth: {
    background: directionBWarmth.appBackground,
    backgroundSubtle: directionBWarmth.panelBackground,
    surface: directionBWarmth.cardBackground,
    surfaceElevated: "rgba(255, 255, 255, 0.97)",
    surfaceMuted: directionBWarmth.panelBackground,
    textPrimary: directionBWarmth.textPrimary,
    textSecondary: directionBWarmth.textSecondary,
    textTertiary: directionBWarmth.textQuiet,
    primaryInverseText: palette.neutral[0],
    borderSubtle: directionBWarmth.paleBlue,
    borderStrong: directionBWarmth.borderBlue,
    accent: directionBWarmth.skyPrimary,
    accentSubtle: directionBWarmth.paleBlue,
    accentStrong: directionBWarmth.skyDeep,
    accentContrast: palette.neutral[0],
    info: directionBWarmth.status.info.text,
    success: directionBWarmth.status.success.text,
    warning: directionBWarmth.status.warning.text,
    danger: directionBWarmth.status.danger.text,
    criticalText: directionBWarmth.status.danger.text,
    headerBand: directionBWarmth.skyDeep,
    headerText: palette.neutral[0],
    headerTextMuted: directionBWarmth.paleBlue,
    backdropOverlay: "rgba(30, 58, 95, 0.58)",
    focusRing: directionBWarmth.tealAccent,
    primaryGradient: directionBWarmth.primaryGradient,
    shellGradient: directionBWarmth.shellGradient,
    onGradientLogoBackground: directionBWarmth.onGradientLogoBackground,
    onGradientPillBackground: directionBWarmth.onGradientPillBackground,
    onGradientPillBorder: directionBWarmth.onGradientPillBorder,
    onGradientAvatarBackground: directionBWarmth.onGradientAvatarBackground,
    onGradientAvatarBorder: directionBWarmth.onGradientAvatarBorder,
    onGradientAvatarText: directionBWarmth.onGradientAvatarText,
    modalHeaderBackground: directionBWarmth.modalHeaderBackground,
    cardShadow: directionBWarmth.cardShadow,
    shellShadow: directionBWarmth.shellShadow,
    statusChip: directionBWarmth.status,
  },
  clinicianWorkbench: {
    background: directionBWarmth.appBackground,
    backgroundSubtle: directionBWarmth.panelBackground,
    surface: palette.neutral[0],
    surfaceElevated: "rgba(255, 255, 255, 0.96)",
    surfaceMuted: directionBWarmth.panelBackground,
    textPrimary: directionBWarmth.textPrimary,
    textSecondary: directionBWarmth.textSecondary,
    textTertiary: directionBWarmth.textQuiet,
    primaryInverseText: palette.neutral[0],
    borderSubtle: directionBWarmth.paleBlue,
    borderStrong: directionBWarmth.borderBlue,
    accent: directionBWarmth.skyPrimary,
    accentSubtle: directionBWarmth.paleBlue,
    accentStrong: directionBWarmth.skyDeep,
    accentContrast: palette.neutral[0],
    info: directionBWarmth.status.info.text,
    success: directionBWarmth.status.success.text,
    warning: directionBWarmth.status.warning.text,
    danger: directionBWarmth.status.danger.text,
    criticalText: directionBWarmth.status.danger.text,
    headerBand: directionBWarmth.skyDeep,
    headerText: palette.neutral[0],
    headerTextMuted: directionBWarmth.paleBlue,
    backdropOverlay: "rgba(239, 248, 255, 0.86)",
    focusRing: directionBWarmth.tealAccent,
    primaryGradient: directionBWarmth.primaryGradient,
    shellGradient: directionBWarmth.shellGradient,
    onGradientLogoBackground: directionBWarmth.onGradientLogoBackground,
    onGradientPillBackground: directionBWarmth.onGradientPillBackground,
    onGradientPillBorder: directionBWarmth.onGradientPillBorder,
    onGradientAvatarBackground: directionBWarmth.onGradientAvatarBackground,
    onGradientAvatarBorder: directionBWarmth.onGradientAvatarBorder,
    onGradientAvatarText: directionBWarmth.onGradientAvatarText,
    modalHeaderBackground: directionBWarmth.modalHeaderBackground,
    cardShadow: directionBWarmth.cardShadow,
    shellShadow: directionBWarmth.shellShadow,
    statusChip: directionBWarmth.status,
  },
  clinicianSlate: {
    background: "#F4F7FA",
    backgroundSubtle: "#F8FAFC",
    surface: palette.neutral[0],
    surfaceElevated: "rgba(255, 255, 255, 0.97)",
    surfaceMuted: "#F8FAFC",
    textPrimary: "#0F172A",
    textSecondary: "#64748B",
    textTertiary: "#64748B",
    primaryInverseText: palette.neutral[0],
    borderSubtle: "#EEF2F6",
    borderStrong: "#E2E8F0",
    accent: "#0D9488",
    accentSubtle: "#CCFBF1",
    accentStrong: "#0F766E",
    accentContrast: palette.neutral[0],
    info: lightStatusChip.info.text,
    success: lightStatusChip.success.text,
    warning: lightStatusChip.warning.text,
    danger: lightStatusChip.danger.text,
    criticalText: lightStatusChip.danger.text,
    headerBand: "#0F2A47",
    headerText: palette.neutral[0],
    headerTextMuted: "#B9CBDF",
    backdropOverlay: "rgba(15, 23, 42, 0.45)",
    focusRing: "#0D9488",
    primaryGradient: "linear-gradient(135deg, #0D9488, #0F766E)",
    shellGradient: "linear-gradient(90deg, #0F2A47, #17436E)",
    onGradientLogoBackground: directionBWarmth.onGradientLogoBackground,
    onGradientPillBackground: "rgba(45, 212, 191, 0.12)",
    onGradientPillBorder: "rgba(45, 212, 191, 0.4)",
    onGradientAvatarBackground: "#134E4A",
    onGradientAvatarBorder: "#134E4A",
    onGradientAvatarText: "#5EEAD4",
    modalHeaderBackground: palette.neutral[0],
    cardShadow: "0 2px 10px rgba(15, 23, 42, 0.05)",
    shellShadow: "0 16px 48px rgba(15, 23, 42, 0.14)",
    statusChip: lightStatusChip,
  },
  // Phase 2a (patient-portal unification): retargeted to the clinicianSlate
  // neutral+teal values so the patient mobile shell matches clinician/admin.
  // Keys are preserved; only values changed. directionBWarmth (sky-blue) is no
  // longer referenced here.
  patientMobileWorkspace: {
    actionBlue: "#0F766E", // -> accentStrong
    actionBlueMuted: "#0D9488", // -> accent
    actionSoft: "#CCFBF1", // -> accentSubtle
    accentTeal: "#0D9488", // -> accent
    agentBlue: "#E2E8F0", // -> borderStrong
    black: "#000000",
    body: "#F4F7FA", // -> background
    darkBlue: "#0F2A47", // -> headerBand
    darkOverlay: "rgba(15, 23, 42, 0.94)",
    detailBackground: "#F4F7FA", // -> background
    fieldBackground: "#F8FAFC", // -> surfaceMuted
    footerBlue: "#0F2A47", // -> headerBand
    footerMuted: "#F8FAFC", // -> surfaceMuted
    ink: "#0F172A", // -> textPrimary
    inputBackground: "#F8FAFC", // -> surfaceMuted
    line: "#EEF2F6", // -> borderSubtle
    mutedText: "#64748B", // -> textSecondary
    nav: palette.neutral[0], // -> surface
    navSelected: "#0F766E", // -> accentStrong
    overlayBorder: "rgba(255,255,255,0.05)",
    primaryText: "#0F172A", // -> textPrimary
    procedureSoft: "#CCFBF1", // -> accentSubtle (unified into teal family)
    softBlue: "#CCFBF1", // -> accentSubtle
    statusInfoText: lightStatusChip.info.text,
    statusWarningBackground: lightStatusChip.warning.background,
    statusWarningText: lightStatusChip.warning.text,
    statusSuccessText: lightStatusChip.success.text,
    successSoft: lightStatusChip.success.background,
    tile: palette.neutral[0],
    white: palette.neutral[0],
    primaryGradient: "linear-gradient(135deg, #0D9488, #0F766E)",
    shellGradient: "linear-gradient(90deg, #0F2A47, #17436E)",
    onGradientAvatarBackground: "#134E4A",
    onGradientAvatarBorder: "#134E4A",
  },
  // Phase 3a (patient-portal unification): retargeted to the clinicianSlate
  // neutral+teal values so the patient desktop shell (and the clinician/admin
  // legacy footer that reuses this palette) matches clinician/admin. Keys are
  // preserved; only values changed.
  patientDesktopWorkspace: {
    actionBlue: "#0F766E", // -> accentStrong
    actionBlueMuted: "#0D9488", // -> accent
    agentBlue: "#E2E8F0", // -> borderStrong
    backdropOverlay: "rgba(15, 23, 42, 0.45)",
    black: "#000000",
    body: "#F4F7FA", // -> background
    darkBlue: "#0F2A47", // -> headerBand
    fieldBackground: "#F8FAFC", // -> surfaceMuted
    footerMuted: "#F8FAFC", // -> surfaceMuted
    headerBlue: "#0F2A47", // -> headerBand
    infoBlue: "#0F766E", // -> accentStrong
    ink: "#0F172A", // -> textPrimary
    inputBackground: "#F8FAFC", // -> surfaceMuted
    inputBorder: "#E2E8F0", // -> borderStrong
    lightBand: "#F8FAFC", // -> surfaceMuted
    lightBlue: "#CCFBF1", // -> accentSubtle
    lightTile: "#EEF2F6", // -> borderSubtle
    line: "#EEF2F6", // -> borderSubtle
    linkBlue: "#0F766E", // -> accentStrong
    mutedText: "#64748B", // -> textSecondary
    placeholderText: "#94A3B8", // -> textTertiary
    primaryText: "#0F172A", // -> textPrimary
    strongBlue: "#0F766E", // -> accentStrong
    tileSelected: "#F4F7FA", // -> background
    white: palette.neutral[0],
    primaryGradient: "linear-gradient(135deg, #0D9488, #0F766E)",
    shellGradient: "linear-gradient(90deg, #0F2A47, #17436E)",
  },
} as const;

export const semanticLayout = {
  cardRadius: radius.xl,
  controlRadius: radius.md,
  modalRadius: radius["2xl"],
  pageGap: spacing.xl,
  sectionGap: spacing.lg,
  compactGap: spacing.sm,
  bodyFontFamily: typography.fontFamily.body,
  displayFontFamily: typography.fontFamily.display,
  bodyFontSize: typography.fontSize.md,
  bodyLineHeight: typography.lineHeight.md,
} as const;

export type SemanticTheme = typeof semanticTheme;
export type SemanticLayout = typeof semanticLayout;
